import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CopyButton } from "~/components/assistant/CopyButton";
import { MARKDOWN_COMPONENTS } from "~/components/assistant/lib/markdownComponents";
import { DetailField } from "~/components/assistant/messages/DetailField";
import { hasContent, unwrapRaw } from "~/components/assistant/messages/toolContent";
import type { ToolCall } from "~/components/assistant/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";

interface ToolContentProps {
  toolCall: ToolCall;
}

export function CreatePlanToolContent({ toolCall }: ToolContentProps) {
  const contentString =
    toolCall.content?.raw || (typeof toolCall.content === "string" ? toolCall.content : null);

  const hasOutput = hasContent(toolCall.content);

  // While running with no output, show input directly without tabs
  if (!hasOutput) {
    return (
      <div className="group relative pt-1">
        <CopyButton
          text={JSON.stringify(toolCall.args, null, 2)}
          variant="light"
          size="sm"
          className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
        />
        <DetailField content={unwrapRaw(toolCall.args)} />
      </div>
    );
  }

  return (
    <Tabs defaultValue="output" className="gap-0">
      <TabsList variant="underlined" className="mb-2">
        <TabsTrigger value="output" className="text-xs py-1.5 pb-2 mr-4">
          Output
        </TabsTrigger>
        <TabsTrigger value="input" className="text-xs py-1.5 pb-2">
          Input
        </TabsTrigger>
      </TabsList>

      <TabsContent value="output">
        {contentString ? (
          <div className="group relative">
            <CopyButton
              text={contentString}
              variant="light"
              size="sm"
              className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
            />
            <div className="prose prose-sm max-w-none text-sm">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
                {contentString}
              </ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="group relative">
            <CopyButton
              text={JSON.stringify(toolCall.content, null, 2)}
              variant="light"
              size="sm"
              className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
            />
            <DetailField content={unwrapRaw(toolCall.content)} />
          </div>
        )}
      </TabsContent>

      <TabsContent value="input">
        <div className="group relative">
          <CopyButton
            text={JSON.stringify(toolCall.args, null, 2)}
            variant="light"
            size="sm"
            className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
          />
          <DetailField content={unwrapRaw(toolCall.args)} />
        </div>
      </TabsContent>
    </Tabs>
  );
}
